import { useState, useEffect, useCallback } from 'react'
import {
  DIMENSION_INFO,
  OVERALL_STABILITY_INTERPRETATIONS,
  TYPE_INTERPRETATIONS,
  GROUP_COLORS,
} from '@mindtype/shared'

// 组别类型列表
const GROUP_TYPES: Record<'AS' | 'AC' | 'RS' | 'RC', { name: string; types: string[] }> = {
  AS: { name: '独立探索', types: ['ASID', 'ASIO', 'ASED', 'ASEO'] },
  AC: { name: '独立创造', types: ['ACID', 'ACIO', 'ACED', 'ACEO'] },
  RS: { name: '专业协作', types: ['RSID', 'RSIO', 'RSED', 'RSEO'] },
  RC: { name: '温暖亲和', types: ['RCID', 'RCIO', 'RCED', 'RCEO'] },
}

// 计算组别统计
function calculateGroupStats(typeDistribution: Record<string, number>) {
  const groupStats: Record<string, { name: string; count: number }> = {}

  Object.entries(GROUP_TYPES).forEach(([key, { name, types }]) => {
    const count = types.reduce((sum, type) => sum + (typeDistribution[type] || 0), 0)
    groupStats[key] = { name, count }
  })

  return groupStats
}

interface AdminStats {
  total: number
  typeDistribution: Record<string, number>
  dimensionDistribution: {
    D1: { A: number; R: number }
    D2: { S: number; C: number }
    D3: { I: number; E: number }
    D4: { D: number; O: number }
  }
  stabilityDistribution: Record<string, number>
  recentSubmissions: number
  demographicStats: {
    gender: Record<string, number>
    ageGroup: Record<string, number>
    currentStatus: Record<string, number>
    industry: Record<string, number>
  }
  crossAnalysis: {
    genderByType: Record<string, Record<string, number>>
    ageByStability: Record<string, Record<string, number>>
    statusByDimension: {
      D1: Record<string, { A: number; R: number }>
      D2: Record<string, { S: number; C: number }>
      D3: Record<string, { I: number; E: number }>
      D4: Record<string, { D: number; O: number }>
    }
  }
  contextSwitchStats: {
    switchRateByDimension: {
      D1: { naturalToWork: number; workToStress: number; naturalToStress: number }
      D2: { naturalToWork: number; workToStress: number; naturalToStress: number }
      D3: { naturalToWork: number; workToStress: number; naturalToStress: number }
      D4: { naturalToWork: number; workToStress: number; naturalToStress: number }
    }
  }
}

// 性别标签映射
const GENDER_LABELS: Record<string, string> = {
  male: '男',
  female: '女',
  other: '其他',
}

// 年龄标签映射
const AGE_LABELS: Record<string, string> = {
  under18: '18岁以下',
  '18-22': '18-22岁',
  '23-25': '23-25岁',
  '26-30': '26-30岁',
  '31-35': '31-35岁',
  over36: '36岁以上',
}

// 状态标签映射
const STATUS_LABELS: Record<string, string> = {
  student_no_exp: '学生(无实习)',
  student_with_exp: '学生(有实习)',
  work_under1: '工作<1年',
  work_1to3: '工作1-3年',
  work_3to5: '工作3-5年',
  work_over5: '工作5年+',
  freelance: '自由职业',
  other_status: '其他',
}

// 职业领域标签映射
const INDUSTRY_LABELS: Record<string, string> = {
  tech: '互联网/科技',
  finance: '金融/咨询',
  manufacturing: '制造/工程',
  education: '教育/科研',
  healthcare: '医疗/健康',
  government: '政府/事业',
  creative: '文化/创意',
  sales: '销售/服务',
  student: '学生',
  other_industry: '其他',
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://mindtype-api.teitatsumi.workers.dev'

// 管理密码
const ADMIN_PASSWORD = '541GeXNH@z'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [authError, setAuthError] = useState('')

  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE}/api/admin/stats`)
      const data = await response.json()
      if (data.success) {
        setStats(data.data)
      } else {
        setError(data.error || '加载失败')
      }
    } catch (err) {
      setError('网络错误，请稍后重试')
    } finally {
      setLoading(false)
    }
  }, [])

  // 检查 sessionStorage 中的登录状态
  useEffect(() => {
    const auth = sessionStorage.getItem('admin_auth')
    if (auth === 'true') {
      setIsAuthenticated(true)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats()
    }
  }, [fetchStats, isAuthenticated])

  const getMostCommonType = (): string => {
    if (!stats?.typeDistribution) return '--'
    const entries = Object.entries(stats.typeDistribution)
    if (entries.length === 0) return '--'
    const sorted = entries.sort((a, b) => b[1] - a[1])
    return sorted[0][0]
  }

  const getTypeName = (code: string): string => {
    const type = TYPE_INTERPRETATIONS[code as keyof typeof TYPE_INTERPRETATIONS]
    return type?.name || code
  }

  const getDimensionPercent = (
    dim: keyof AdminStats['dimensionDistribution'],
    pole: string
  ): number => {
    if (!stats?.dimensionDistribution) return 0
    const dimData = stats.dimensionDistribution[dim]
    const total = Object.values(dimData).reduce((a, b) => a + b, 0)
    if (total === 0) return 0
    return Math.round(((dimData[pole as keyof typeof dimData] || 0) / total) * 100)
  }

  const getStabilityPercent = (type: string): number => {
    if (!stats?.stabilityDistribution || stats.total === 0) return 0
    return Math.round(((stats.stabilityDistribution[type] || 0) / stats.total) * 100)
  }

  // 获取性别下最常见的类型
  const getTopTypeByGender = (gender: string): string => {
    const types = stats?.crossAnalysis?.genderByType?.[gender]
    if (!types) return '--'
    const entries = Object.entries(types)
    if (entries.length === 0) return '--'
    const sorted = entries.sort((a, b) => b[1] - a[1])
    return sorted[0][0]
  }

  const sortedTypes = stats?.typeDistribution
    ? Object.entries(stats.typeDistribution).sort((a, b) => b[1] - a[1])
    : []

  const maxTypeCount = sortedTypes.length > 0 ? sortedTypes[0][1] : 1

  // 未登录时显示登录界面
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-sm w-full">
          <h1 className="text-xl font-bold text-center mb-6">MindType Admin</h1>
          <form onSubmit={(e) => {
            e.preventDefault()
            if (password === ADMIN_PASSWORD) {
              setIsAuthenticated(true)
              setAuthError('')
              sessionStorage.setItem('admin_auth', 'true')
            } else {
              setAuthError('密码错误')
            }
          }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入管理密码"
              className="w-full px-4 py-2 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {authError && <p className="text-red-500 text-sm mb-4">{authError}</p>}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              登录
            </button>
          </form>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchStats}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            重试
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">MindType Admin</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchStats}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              刷新
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem('admin_auth')
                setIsAuthenticated(false)
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              退出
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="总提交数"
            value={stats?.total || 0}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
          <StatCard
            title="近7天"
            value={stats?.recentSubmissions || 0}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          />
          <StatCard
            title="最常见类型"
            value={getMostCommonType()}
            subtitle={getTypeName(getMostCommonType())}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
          <StatCard
            title="类型数量"
            value={Object.keys(stats?.typeDistribution || {}).length}
            subtitle="/ 16 种类型"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            }
          />
        </div>

        {/* Group Distribution */}
        {stats?.typeDistribution && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">组别分布</h2>

            {/* 四个组的卡片 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {Object.entries(calculateGroupStats(stats.typeDistribution)).map(([key, group]) => {
                const percent = stats.total > 0 ? ((group.count / stats.total) * 100).toFixed(1) : '0'
                const groupColors = GROUP_COLORS[key as keyof typeof GROUP_COLORS]

                return (
                  <div
                    key={key}
                    className="p-4 rounded-lg border-l-4"
                    style={{
                      borderColor: groupColors.primary,
                      backgroundColor: `${groupColors.primary}10`,
                    }}
                  >
                    <div className="text-sm text-gray-500">{key} · {group.name}</div>
                    <div className="text-2xl font-bold" style={{ color: groupColors.primary }}>
                      {group.count}
                    </div>
                    <div className="text-sm text-gray-400">{percent}%</div>
                  </div>
                )
              })}
            </div>

            {/* 条形图对比 */}
            <div className="space-y-2">
              {Object.entries(calculateGroupStats(stats.typeDistribution)).map(([key, group]) => {
                const percent = stats.total > 0 ? (group.count / stats.total) * 100 : 0
                const groupColors = GROUP_COLORS[key as keyof typeof GROUP_COLORS]

                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className="w-24 text-sm text-gray-700">{key} {group.name}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${percent}%`,
                          backgroundColor: groupColors.primary,
                        }}
                      />
                    </div>
                    <span className="w-20 text-sm text-right text-gray-600">
                      {group.count} ({percent.toFixed(0)}%)
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Type Distribution */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">类型分布</h2>
          {sortedTypes.length > 0 ? (
            <div className="space-y-3">
              {sortedTypes.map(([type, count]) => (
                <div key={type} className="flex items-center gap-4">
                  <div className="w-16 font-mono text-sm text-gray-700">{type}</div>
                  <div className="flex-1">
                    <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                        style={{ width: `${(count / maxTypeCount) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-12 text-right text-sm text-gray-600">{count}</div>
                  <div className="w-32 text-sm text-gray-500 truncate">
                    {getTypeName(type)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">暂无数据</p>
          )}
        </div>

        {/* Dimension Distribution */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">维度分布</h2>
          <div className="space-y-4">
            {(['D1', 'D2', 'D3', 'D4'] as const).map((dim) => {
              const dimInfo = DIMENSION_INFO[dim]
              const poleA = dimInfo.poleA
              const poleB = dimInfo.poleB
              const percentA = getDimensionPercent(dim, poleA.letter)
              const percentB = getDimensionPercent(dim, poleB.letter)

              return (
                <div key={dim} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">
                      {dim} {dimInfo.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 text-right text-sm">
                      <span className="font-mono text-indigo-600">{poleA.letter}</span>
                      <span className="text-gray-600 ml-1">{poleA.name}</span>
                    </div>
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-indigo-500 transition-all duration-500"
                        style={{ width: `${percentA}%` }}
                      />
                      <div
                        className="h-full bg-amber-500 transition-all duration-500"
                        style={{ width: `${percentB}%` }}
                      />
                    </div>
                    <div className="w-24 text-sm">
                      <span className="font-mono text-amber-600">{poleB.letter}</span>
                      <span className="text-gray-600 ml-1">{poleB.name}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{percentA}%</span>
                    <span>{percentB}%</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Stability Distribution */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">稳定性分布</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(OVERALL_STABILITY_INTERPRETATIONS).map(([type, info]) => {
              const percent = getStabilityPercent(type)
              const count = stats?.stabilityDistribution?.[type] || 0

              return (
                <div key={type} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">{info.name}</span>
                    <span className="text-sm text-gray-500">{count} 人</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="text-right text-sm text-gray-600">{percent}%</div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Section Divider */}
        <div className="border-t border-gray-200 my-8">
          <h2 className="text-xl font-bold text-gray-900 -mt-3 bg-gray-50 inline-block pr-4">
            人口统计分析
          </h2>
        </div>

        {/* Demographic Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Gender Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">性别分布</h3>
            {Object.keys(stats?.demographicStats?.gender || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats?.demographicStats?.gender || {}).map(([key, count]) => {
                  const total = Object.values(stats?.demographicStats?.gender || {}).reduce((a, b) => a + b, 0)
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-20 text-sm text-gray-700">{GENDER_LABELS[key] || key}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-pink-500 rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="w-16 text-right text-sm text-gray-600">{count} ({percent}%)</div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">暂无数据</p>
            )}
          </div>

          {/* Age Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">年龄分布</h3>
            {Object.keys(stats?.demographicStats?.ageGroup || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats?.demographicStats?.ageGroup || {}).map(([key, count]) => {
                  const total = Object.values(stats?.demographicStats?.ageGroup || {}).reduce((a, b) => a + b, 0)
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-24 text-sm text-gray-700">{AGE_LABELS[key] || key}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="w-16 text-right text-sm text-gray-600">{count} ({percent}%)</div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">暂无数据</p>
            )}
          </div>

          {/* Status Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">当前状态分布</h3>
            {Object.keys(stats?.demographicStats?.currentStatus || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats?.demographicStats?.currentStatus || {}).map(([key, count]) => {
                  const total = Object.values(stats?.demographicStats?.currentStatus || {}).reduce((a, b) => a + b, 0)
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-28 text-sm text-gray-700 truncate" title={STATUS_LABELS[key] || key}>
                        {STATUS_LABELS[key] || key}
                      </div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="w-16 text-right text-sm text-gray-600">{count} ({percent}%)</div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">暂无数据</p>
            )}
          </div>

          {/* Industry Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">职业领域分布</h3>
            {Object.keys(stats?.demographicStats?.industry || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats?.demographicStats?.industry || {}).map(([key, count]) => {
                  const total = Object.values(stats?.demographicStats?.industry || {}).reduce((a, b) => a + b, 0)
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-28 text-sm text-gray-700 truncate" title={INDUSTRY_LABELS[key] || key}>
                        {INDUSTRY_LABELS[key] || key}
                      </div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-purple-500 rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <div className="w-16 text-right text-sm text-gray-600">{count} ({percent}%)</div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">暂无数据</p>
            )}
          </div>
        </div>

        {/* Section Divider */}
        <div className="border-t border-gray-200 my-8">
          <h2 className="text-xl font-bold text-gray-900 -mt-3 bg-gray-50 inline-block pr-4">
            交叉分析
          </h2>
        </div>

        {/* Cross Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Gender by Type */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">性别 × 最常见类型</h3>
            {Object.keys(stats?.crossAnalysis?.genderByType || {}).length > 0 ? (
              <div className="space-y-4">
                {Object.keys(stats?.crossAnalysis?.genderByType || {}).map((gender) => {
                  const topType = getTopTypeByGender(gender)
                  const typeCount = stats?.crossAnalysis?.genderByType?.[gender]?.[topType] || 0
                  return (
                    <div key={gender} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-700">{GENDER_LABELS[gender] || gender}</span>
                      <div className="text-right">
                        <span className="font-mono text-indigo-600 font-semibold">{topType}</span>
                        <span className="text-gray-500 text-sm ml-2">({typeCount}人)</span>
                        <div className="text-xs text-gray-400">{getTypeName(topType)}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">暂无数据</p>
            )}
          </div>

          {/* Age by Stability */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">年龄 × 稳定性</h3>
            {Object.keys(stats?.crossAnalysis?.ageByStability || {}).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-gray-600">年龄段</th>
                      {Object.keys(OVERALL_STABILITY_INTERPRETATIONS).map((type) => (
                        <th key={type} className="text-center py-2 text-gray-600 px-2">
                          {OVERALL_STABILITY_INTERPRETATIONS[type as keyof typeof OVERALL_STABILITY_INTERPRETATIONS].name.replace('型', '')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats?.crossAnalysis?.ageByStability || {}).map(([age, stabilityData]) => (
                      <tr key={age} className="border-b">
                        <td className="py-2 text-gray-700">{AGE_LABELS[age] || age}</td>
                        {Object.keys(OVERALL_STABILITY_INTERPRETATIONS).map((type) => (
                          <td key={type} className="text-center py-2 px-2">
                            <span className={`inline-block min-w-[24px] ${(stabilityData as Record<string, number>)[type] ? 'text-indigo-600 font-medium' : 'text-gray-300'}`}>
                              {(stabilityData as Record<string, number>)[type] || 0}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">暂无数据</p>
            )}
          </div>
        </div>

        {/* Section Divider */}
        <div className="border-t border-gray-200 my-8">
          <h2 className="text-xl font-bold text-gray-900 -mt-3 bg-gray-50 inline-block pr-4">
            情境切换分析
          </h2>
        </div>

        {/* Context Switch Stats */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">各维度情境切换率</h3>
          <p className="text-sm text-gray-500 mb-4">
            显示在不同情境转换时，各维度的极点发生变化的比例
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 text-gray-600">维度</th>
                  <th className="text-center py-3 text-gray-600">日常 → 工作</th>
                  <th className="text-center py-3 text-gray-600">工作 → 压力</th>
                  <th className="text-center py-3 text-gray-600">日常 → 压力</th>
                </tr>
              </thead>
              <tbody>
                {(['D1', 'D2', 'D3', 'D4'] as const).map((dim) => {
                  const dimInfo = DIMENSION_INFO[dim]
                  const switchData = stats?.contextSwitchStats?.switchRateByDimension?.[dim]
                  return (
                    <tr key={dim} className="border-b">
                      <td className="py-3">
                        <span className="font-medium text-gray-700">{dim}</span>
                        <span className="text-gray-500 ml-2">{dimInfo.name}</span>
                      </td>
                      <td className="text-center py-3">
                        <SwitchRateCell value={switchData?.naturalToWork || 0} />
                      </td>
                      <td className="text-center py-3">
                        <SwitchRateCell value={switchData?.workToStress || 0} />
                      </td>
                      <td className="text-center py-3">
                        <SwitchRateCell value={switchData?.naturalToStress || 0} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-xs text-gray-400">
            * 切换率越高表示该维度在对应情境转换时越容易发生变化
          </div>
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
}

function StatCard({ title, value, subtitle, icon }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500">{title}</span>
        <span className="text-gray-400">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
    </div>
  )
}

function SwitchRateCell({ value }: { value: number }) {
  const bgColor = value === 0 ? 'bg-gray-100' :
    value < 20 ? 'bg-green-100 text-green-700' :
    value < 40 ? 'bg-yellow-100 text-yellow-700' :
    'bg-red-100 text-red-700'

  return (
    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${bgColor}`}>
      {value}%
    </span>
  )
}

export default App
